import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import './Signup.css';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import Select from 'react-select';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const Signup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    surname: '',
    phone: '',
    is_landlord: false,
    is_subscribed: false,
    heard_from: '',
    referred_by_code: '',
    location: '',
    city_id: null,
    city: null
  });

  const [confirmPassword, setConfirmPassword] = useState('');

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showHeardFromOptions, setShowHeardFromOptions] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [locationOptions, setLocationOptions] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [cities, setCities] = useState([]);
  const [referralLink, setReferralLink] = useState('');

  const baseUrl = 'https://api.homehive.co.zw';

  const heardFromOptions = [
    { value: 'facebook', label: 'Facebook' },
    { value: 'tiktok', label: 'TikTok' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'friend', label: 'From a Friend' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'confirm_password') {
      setConfirmPassword(value);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleRoleSelect = (isLandlord) => {
    setFormData(prev => ({
      ...prev,
      is_landlord: isLandlord
    }));
    setStep(2);
  };

  const handleHeardFromClick = () => {
    setShowHeardFromOptions(!showHeardFromOptions);
  };

  const handleOptionSelect = (value) => {
    setFormData(prev => ({
      ...prev,
      heard_from: value,
      referred_by_code: value === 'friend' ? prev.referral_code : ''
    }));
    setShowHeardFromOptions(false);

    // Clear error if exists
    if (errors.heard_from) {
      setErrors(prev => ({ ...prev, heard_from: '' }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.surname.trim()) newErrors.surname = 'Surname is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone) {
      newErrors.phone = 'Phone is required';
    } else {
      // Remove any spaces and non-digit characters except +
      const cleanPhone = formData.phone.replace(/[^\d+]/g, '');
      // Check if it starts with +263 and has correct length
      if (!cleanPhone.startsWith('+263')) {
        newErrors.phone = 'Phone number must start with +263';
      } else if (cleanPhone.length !== 13) { // +263 + 10 digits
        newErrors.phone = 'Phone number must be 10 digits after +263';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;

    // If empty, set to +263
    if (!value) {
      value = '+263';
    }
    // If user is typing and hasn't added +263, add it
    else if (!value.startsWith('+263')) {
      value = '+263' + value.replace(/[^\d]/g, '');
    }

    // Update the form data
    setFormData(prev => ({
      ...prev,
      phone: value
    }));

    // Clear error when user is typing
    if (errors.phone) {
      setErrors(prev => ({
        ...prev,
        phone: ''
      }));
    }
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (formData.password !== confirmPassword) {
      newErrors.confirm_password = 'Passwords do not match';
    }
    if (!formData.heard_from) {
      newErrors.heard_from = 'Please select how you heard about us';
    }
    if (formData.heard_from === 'friend' && !formData.referred_by_code) {
      newErrors.referred_by_code = 'Referral code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (step === 2) {
      if (validateStep1()) {
        setStep(3);
      }
    } else if (step === 3) {
      if (validateStep2()) {
        handleSubmit(e);
      }
    }
  };

  const handlePrev = () => {
    if (step === 2) {
      setStep(1);
    } else if (step === 3) {
      setStep(2);
    }
  };

  const renderStepIndicator = () => (
    <div className="signup-step-indicator">
      <div className={`step-dot ${step === 1 ? 'active' : ''}`} />
      <div className={`step-dot ${step === 2 ? 'active' : ''}`} />
      <div className={`step-dot ${step === 3 ? 'active' : ''}`} />
    </div>
  );

  const handleLocationSelect = (selectedOption) => {
    if (selectedOption) {
      setFormData(prev => ({
        ...prev,
        city_id: selectedOption.value.id,
        city: selectedOption.value.name
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        city_id: null,
        city: null
      }));
    }

    if (errors.location) {
      setErrors(prev => ({ ...prev, location }));
    }
  };

  const renderRoleSelection = () => (
    <div className="role-selection-container">
      <p className="role-selection-subtitle">Choose your role to continue</p>

      <div className="role-cards">
        <div
          className="role-card tenant-card"
          onClick={() => handleRoleSelect(false)}
        >
          <div className="role-icon">
            <i className="fas fa-home"></i>
          </div>
          <h3>Tenant</h3>
          <p>I'm looking for a property to rent</p>
          <div className="role-cta">
            <span>Continue</span>
            <i className="fas fa-arrow-right"></i>
          </div>
        </div>

        <div
          className="role-card landlord-card"
          onClick={() => handleRoleSelect(true)}
        >
          <div className="role-icon">
            <i className="fas fa-key"></i>
          </div>
          <h3>Landlord</h3>
          <p>I'm a landlord looking to rent out my property</p>
          <div className="role-cta">
            <span>Continue</span>
            <i className="fas fa-arrow-right"></i>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPersonalInfo = () => (
    <form onSubmit={handleNext} className="signup-form">
      <div className="signup-form-group">
        <label className="signup-label">First Name</label>
        <input
          type="text"
          name="name"
          className={`signup-input ${errors.name ? 'error' : ''}`}
          value={formData.name}
          onChange={handleChange}
          placeholder="First name"
        />
        {errors.name && <div className="error-text">{errors.name}</div>}
      </div>

      <div className="signup-form-group">
        <label className="signup-label">Last Name</label>
        <input
          type="text"
          name="surname"
          className={`signup-input ${errors.surname ? 'error' : ''}`}
          value={formData.surname}
          onChange={handleChange}
          placeholder="Last name"
        />
        {errors.surname && <div className="error-text">{errors.surname}</div>}
      </div>

      <div className="signup-form-group">
        <label className="signup-label">Email</label>
        <input
          type="email"
          name="email"
          className={`signup-input ${errors.email ? 'error' : ''}`}
          value={formData.email}
          onChange={handleChange}
          placeholder="Enter your email"
        />
        {errors.email && <div className="error-text">{errors.email}</div>}
      </div>

      <div className="signup-form-group">
        <label className="signup-label">Phone Number</label>
        <input
          type="tel"
          name="phone"
          className={`signup-input ${errors.phone ? 'error' : ''}`}
          value={formData.phone}
          onChange={handlePhoneChange}
          placeholder="+263"
        />
        {errors.phone && <div className="error-text">{errors.phone}</div>}
      </div>

      <div className="signup-form-group full-width">
        <div className="step-buttons">
          <button
            type="button"
            className="prev-button"
            onClick={handlePrev}
          >
            <i className="fas fa-arrow-left"></i>
            Back
          </button>
          <button type="submit" className="next-button">
            Next
            <i className="fas fa-arrow-right"></i>
          </button>
        </div>
      </div>
    </form>
  );

  const renderStep2 = () => (
    <form onSubmit={handleNext} className="signup-form">
      <div className="signup-form-group">
        <label className="signup-label">Password</label>
        <input
          type="password"
          name="password"
          className={`signup-input ${errors.password ? 'error' : ''}`}
          value={formData.password}
          onChange={handleChange}
          placeholder="Create a password"
        />
        {errors.password && <div className="error-text">{errors.password}</div>}
      </div>

      <div className="signup-form-group">
        <label className="signup-label">Confirm Password</label>
        <input
          type="password"
          name="confirm_password"
          className={`signup-input ${errors.confirm_password ? 'error' : ''}`}
          value={confirmPassword}
          onChange={handleChange}
          placeholder="Confirm your password"
        />
        {errors.confirm_password && (
          <div className="error-text">{errors.confirm_password}</div>
        )}
      </div>

      <div className="signup-form-group">
        <label className="signup-label">How did you hear about us?</label>
        <div className={`heard-from-select ${errors.heard_from ? 'error' : ''}`}>
          <div
            className="custom-select"
            onClick={handleHeardFromClick}
          >
            {formData.heard_from ? (
              <div className="selected-option">
                <i className={`fab fa-${formData.heard_from === 'friend' ? 'user-friends' : formData.heard_from}`}></i>
                <span>{heardFromOptions.find(option => option.value === formData.heard_from)?.label || 'Select option'}</span>
              </div>
            ) : (
              <span>Select option</span>
            )}
          </div>
          {showHeardFromOptions && (
            <div className="options-list">
              {heardFromOptions.map((option) => (
                <div
                  key={option.value}
                  className={`option ${formData.heard_from === option.value ? 'selected' : ''} ${option.value}`}
                  onClick={() => handleOptionSelect(option.value)}
                >
                  <i className={`${option.value === 'friend' ? 'fas fa-user-friends' : `fab fa-${option.value}`}`}></i>
                  <span>{option.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {errors.heard_from && <div className="error-text">{errors.heard_from}</div>}
      </div>

      {formData.heard_from === 'friend' && (
        <div className="signup-form-group">
          <label className="signup-label">Friend's Referral Code</label>
          <input
            type="text"
            name="referred_by_code"
            className={`signup-input ${errors.referred_by_code ? 'error' : ''}`}
            value={formData.referred_by_code || (new URLSearchParams(window.location.search).get('ref') || '')}
            onChange={handleChange}
            readOnly={new URLSearchParams(window.location.search).has('ref')}
            placeholder="Enter your friend's referral code"
          />
          {errors.referred_by_code && <div className="error-text">{errors.referred_by_code}</div>}
        </div>
      )}

      <div className="signup-form-group full-width">
        <div className="step-buttons">
          <button
            type="button"
            className="prev-button"
            onClick={handlePrev}
          >
            <i className="fas fa-arrow-left"></i>
            Back
          </button>
          <button
            type="submit"
            className="next-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span>Creating Account</span>
                <i className="fas fa-spinner fa-spin"></i>
              </>
            ) : (
              <>
                <span>Create Account</span>
                <i className="fas fa-arrow-right"></i>
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Final validation
    if (!validateStep2()) {
      return;
    }

    setIsLoading(true);

    try {
      // Prepare the data
      const userData = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        surname: formData.surname,
        phone: formData.phone,
        is_landlord: formData.is_landlord,
        heard_from: formData.heard_from,
        is_subscribed: formData.is_subscribed,
      };

      // Add referral code if provided
      if (formData.heard_from === 'friend' && formData.referred_by_code) {
        userData.referred_by_code = formData.referred_by_code;
      }

      // Add location if available
      if (formData.city_id) {
        userData.city_id = formData.city_id;
      }

      const response = await axios.post(`${baseUrl}/api/signup/`, userData);

      if (response.status === 201) {
        Swal.fire({
          title: 'Almost Done!',
          text: 'Account created successfully.',
          icon: 'success',
          confirmButtonColor: '#1a237e',
          confirmButtonText: 'OK'
        }).then(() => {
          navigate('/login');
        });
      } else {
        throw new Error('Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);

      let errorMessage = 'Registration failed. Please try again.';

      if (error.response) {
        if (error.response.status === 400) {
          const responseData = error.response.data;
          const errorFields = Object.keys(responseData);

          if (errorFields.length > 0) {
            const newErrors = { ...errors };

            errorFields.forEach(field => {
              if (field === 'email' && responseData.email.includes('already exists')) {
                newErrors.email = 'This email is already registered';
                errorMessage = 'This email is already registered.';
              } else if (field === 'phone' && responseData.phone.includes('already exists')) {
                newErrors.phone = 'This phone number is already registered';
                errorMessage = 'This phone number is already registered.';
              } else if (field === 'referred_by_code') {
                newErrors.referred_by_code = 'Invalid referral code';
                errorMessage = 'Invalid referral code.';
              } else {
                newErrors[field] = responseData[field][0];
                errorMessage = responseData[field][0];
              }
            });

            setErrors(newErrors);
          }
        }
      }

      Swal.fire({
        title: 'Registration Failed',
        text: errorMessage,
        icon: 'error',
        confirmButtonColor: '#1a237e'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Add this useEffect to handle clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const select = document.querySelector('.select-wrapper');
      if (select && !select.contains(event.target)) {
        setShowHeardFromOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Add this useEffect to fetch cities when component mounts
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/cities`);
        console.log(response.data);
        const cityOptions = response.data.map(city => ({
          value: {
            name: city.name,
            id: city.id,
          },
          label: `${city.name}`
        }));
        setCities(cityOptions);
      } catch (error) {
        console.error('Error fetching cities:', error);
      }
    };

    fetchCities();
  }, []);

  // Add this function to get referral code from URL
  const getReferralCodeFromUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('ref');
  };

  // Add this useEffect to handle referral code from URL
  useEffect(() => {
    const referralCode = getReferralCodeFromUrl();
    if (referralCode) {
      setFormData(prev => ({
        ...prev,
        heard_from: 'friend',
        referred_by_code: referralCode
      }));
    }
  }, [location.search]);


  return (
    <div style={{ overflow: 'hidden' }} className="signup-wrapper">
      <div className="signup-overlay"></div>
      <Link to="/" className="back-button">
        <i className="fas fa-arrow-left"></i>
        <span>Back to Home</span>
      </Link>
      <div className="signup-container">
        <div className="signup-card">
          <div className="signup-header">
            <h1 className="signup-title">Create Your Account</h1>
            <p className="signup-subtitle">
              {step === 1
                ? "Join Home Hive today and find your perfect space"
                : step === 2
                  ? "Let's get to know you better"
                  : "Set up your account security"}
            </p>
          </div>

          {renderStepIndicator()}

          {step === 1 ? renderRoleSelection() : step === 2 ? renderPersonalInfo() : renderStep2()}

          <div className="login-redirect">
            Already have an account? <Link to="/login" className="login-link">Login</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;